import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { DELETE_CUSTOM_LABEL, GET_CUSTOM_LABELS } from '@moderation/graphql';
import { useState, useCallback } from 'react';

interface DeleteCustomLabelResponse {
  deleteCustomLabel: {
    success: boolean;
  };
}

/**
 * Hook to delete a custom label
 * @param communityId The ID of the community
 * @returns Mutation function, loading state, errors, and utilities
 */
export function useDeleteCustomLabel(communityId: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [deleteCustomLabelMutation, { loading }] = useMutation<DeleteCustomLabelResponse>(DELETE_CUSTOM_LABEL);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const deleteCustomLabel = async (labelId: string): Promise<boolean> => {
    try {
      track('DeleteCustomLabelInitiated', {
        communityId,
        labelId,
      });

      await deleteCustomLabelMutation({
        variables: { communityId, labelId },
        // Cache sync: refetch custom labels after deletion to ensure list consistency
        refetchQueries: [{ query: GET_CUSTOM_LABELS, variables: { communityId } }],
        awaitRefetchQueries: true,
      });

      track('DeleteCustomLabelSucceeded', {
        communityId,
        labelId,
      });

      return true;
    } catch (error) {
      track('DeleteCustomLabelFailed', {
        communityId,
        labelId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    deleteCustomLabel,
    loading,
    errors,
    resetErrors,
  };
}
